import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Wrench, FileText, Calendar, Star, Sofa, Droplets } from "lucide-react";
import Hero from "@/components/sections/Hero";
import WhyUs from "@/components/sections/WhyUs";

const NETTOYAGE_IMG = "https://images.unsplash.com/photo-1605164599901-db7f68c4b175?w=1200&q=80&auto=format&fit=crop";
const MECANIQUE_IMG = "https://images.unsplash.com/photo-1775285157647-73f29f910216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjYXIlMjBlbmdpbmUlMjBtZWNoYW5pY3xlbnwwfHx8fDE3NzgxNDQ1ODF8MA&ixlib=rb-4.1.0&q=85";
const TEXTILE_IMG = "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1200&q=80&auto=format&fit=crop";
const SURFACES_IMG = "https://images.unsplash.com/photo-1610824352934-c10d87b700cc?w=1200&q=80&auto=format&fit=crop";

export default function Home() {
  return (
    <>
      <Hero />

      {/* Two main universes */}
      <section id="univers" className="relative py-28 lg:py-36 border-t border-white/5" data-testid="home-univers">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
              / Nos univers
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
              Plusieurs savoir-faire,<br />
              <span className="text-gradient-red">un seul service à domicile.</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Nettoyage automobile, textile maison, surfaces extérieures et petite mécanique : tout chez vous, sans déplacement de votre côté.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <UniverseCard
              to="/nettoyage"
              testid="univers-nettoyage"
              image={NETTOYAGE_IMG}
              icon={Sparkles}
              eyebrow="Detailing premium"
              title="Nettoyage automobile"
              desc="Intérieur, extérieur, pack complet ou Formule Concession Premium. Tarifs transparents par catégorie de véhicule."
              priceLabel="À partir de"
              price="50€"
              cta="Découvrir nos formules"
            />
            <UniverseCard
              to="/textile"
              testid="univers-textile"
              image={TEXTILE_IMG}
              icon={Sofa}
              eyebrow="Textile maison"
              title="Canapés, matelas & chaises"
              desc="Nettoyage en profondeur de vos textiles d'intérieur, à domicile. Shampouineuse pro, séchage rapide."
              priceLabel="À partir de"
              price="20€"
              cta="Voir les tarifs"
            />
            <UniverseCard
              to="/mecanique"
              testid="univers-mecanique"
              image={MECANIQUE_IMG}
              icon={Wrench}
              eyebrow="Atelier mobile"
              title="Petite mécanique"
              desc="Vidange, freinage, batterie, démarreur, alternateur… Devis personnalisé selon votre véhicule, en 24h."
              priceLabel="Tarif"
              price="Sur devis"
              cta="Demander un devis"
            />
            <UniverseCard
              to="/surfaces"
              testid="univers-surfaces"
              image={SURFACES_IMG}
              icon={Droplets}
              eyebrow="Haute pression"
              title="Surfaces extérieures"
              desc="Box, balcon, parking, terrasses, cours et locaux commerciaux. Nettoyage haute pression, au m² ou sur devis."
              priceLabel="À partir de"
              price="4€/m²"
              cta="Voir les tarifs"
            />
          </div>
        </div>
      </section>

      <WhyUs />

      {/* Quick navigation tiles */}
      <section className="relative py-20 lg:py-24 border-t border-white/5" data-testid="home-quick-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
            / Aller plus loin
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-12">
            Explorez Nova Clean Service.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickTile to="/galerie" icon={Sparkles} label="Galerie Avant / Après" sub="Voir nos résultats" testid="qt-galerie" />
            <QuickTile to="/avis" icon={Star} label="Avis clients" sub="Témoignages vérifiés" testid="qt-avis" />
            <QuickTile to="/nettoyage#reserver" icon={Calendar} label="Réserver un créneau" sub="Nettoyage en ligne" testid="qt-reserver" />
            <QuickTile to="/contact" icon={FileText} label="Nous contacter" sub="Devis & informations" testid="qt-contact" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[#E50914]/10 blur-[160px] rounded-full"></div>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
            / Prêt à passer à l'action
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
            Votre voiture mérite<br />
            <span className="text-gradient-red">le meilleur traitement.</span>
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
            Réservez en ligne ou demandez un devis personnalisé. On revient vers vous sous 24h, garanti.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/nettoyage#reserver"
              data-testid="home-final-cta-reserver"
              className="btn-sheen inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider px-8 py-4 rounded-lg border border-[#E50914] shadow-[0_8px_30px_-8px_rgba(229,9,20,0.6)] hover:shadow-[0_0_36px_rgba(229,9,20,0.6)] transition-all"
            >
              Réserver un nettoyage <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/mecanique"
              data-testid="home-final-cta-mecanique"
              className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/20 hover:bg-white/[0.08] hover:border-white/40 text-white font-semibold uppercase text-sm tracking-wider px-8 py-4 rounded-lg transition-all"
            >
              Devis mécanique
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function UniverseCard({ to, image, icon: Icon, eyebrow, title, desc, priceLabel, price, cta, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#E50914]/50 bg-[#0A0A0A] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_-30px_rgba(0,0,0,0.9),0_0_0_1px_rgba(229,9,20,0.12)] flex flex-col min-h-[420px]"
    >
      {/* Image */}
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent"></div>
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 bg-black/70 backdrop-blur-sm border border-[#E50914]/40 rounded-full">
          <Icon className="w-3.5 h-3.5 text-[#E50914]" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white">{eyebrow}</span>
        </div>
      </div>

      <div className="relative p-7 flex flex-col flex-1">
        <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">{title}</h3>
        <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">{desc}</p>

        <div className="flex items-center justify-between pt-5 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{priceLabel}</div>
            <div className="text-2xl font-black text-white">{price}</div>
          </div>
          <div className="inline-flex items-center gap-2 text-[#E50914] font-bold uppercase text-xs tracking-wider group-hover:gap-3 transition-all">
            {cta} <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function QuickTile({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group p-5 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl hover:border-[#E50914]/40 hover:-translate-y-1 transition-all flex items-center gap-4"
    >
      <div className="w-11 h-11 rounded-lg bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#E50914]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-white text-sm leading-tight">{label}</div>
        <div className="text-xs text-white/50 mt-0.5">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#E50914] group-hover:translate-x-1 transition-all" />
    </Link>
  );
}
